// src/services/riskAssessment.js
// Orchestrates data fetching and computes per-activity risk levels.
// Returns detailed factor-by-factor breakdown for UI transparency.

import { fetchAQI } from "./airQuality";
import { fetchNWSAlerts } from "./nwsAlerts";
import activityThresholds, { RISK_LEVELS } from "../constants/activityThresholds";

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Parse wind speed from weather.gov period (e.g. "10 to 20 mph" → 20) */
function parseWindSpeed(windStr) {
  if (!windStr) return 0;
  const nums = windStr.match(/\d+/g);
  if (!nums) return 0;
  return Math.max(...nums.map(Number));
}

/** Determine the worst severity from an array of NWS alert properties */
function worstNWSSeverity(alerts) {
  const order = ["minor", "moderate", "severe", "extreme"];
  let worst = -1;
  for (const a of alerts) {
    const idx = order.indexOf((a.severity || "").toLowerCase());
    if (idx > worst) worst = idx;
  }
  if (worst < 0) return null;
  return order[worst];
}

/** Check if any text in forecastPeriods contains a keyword (case-insensitive) */
function forecastContains(periods, keyword) {
  if (!periods || !keyword) return false;
  const kw = keyword.toLowerCase();
  return periods.some(
    (p) =>
      (p.shortForecast || "").toLowerCase().includes(kw) ||
      (p.detailedForecast || "").toLowerCase().includes(kw)
  );
}

// ─── Primary data fetch ─────────────────────────────────────────────────────

/**
 * Fetch all raw data needed for risk assessment at a given location.
 * @param {number} lat
 * @param {number} lon
 * @returns {Promise<object>} { weather, aqi, nwsAlerts, errors }
 */
export async function fetchRiskData(lat, lon) {
  const errors = {};
  let weather = null;
  let aqi = null;
  let nwsAlerts = [];

  // Weather forecast from weather.gov
  const weatherPromise = (async () => {
    try {
      const WEATHER_API_BASE = process.env.REACT_APP_WEATHER_API || "https://api.weather.gov";
      const pointsRes = await Promise.race([
        fetch(`${WEATHER_API_BASE}/points/${lat},${lon}`, {
          headers: { "User-Agent": "(outdoorec-app, contact@outdoorec.com)" },
        }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000)),
      ]);
      if (!pointsRes.ok) throw new Error(`Grid: ${pointsRes.status}`);
      const pointsJson = await pointsRes.json();
      const forecastUrl = pointsJson?.properties?.forecast;
      if (!forecastUrl) throw new Error("No forecast URL");
      const fcRes = await Promise.race([
        fetch(forecastUrl, {
          headers: { "User-Agent": "(outdoorec-app, contact@outdoorec.com)" },
        }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 15000)),
      ]);
      if (!fcRes.ok) throw new Error(`Forecast: ${fcRes.status}`);
      weather = await fcRes.json();
    } catch (e) {
      errors.weather = e.message;
    }
  })();

  // AQI
  const aqiPromise = (async () => {
    try {
      aqi = await fetchAQI(lat, lon);
    } catch (e) {
      errors.aqi = e.message;
    }
  })();

  // NWS active alerts
  const nwsPromise = (async () => {
    try {
      nwsAlerts = await fetchNWSAlerts(lat, lon);
    } catch (e) {
      errors.nwsAlerts = e.message;
    }
  })();

  await Promise.all([weatherPromise, aqiPromise, nwsPromise]);

  return { weather, aqi, nwsAlerts, errors };
}

// ─── Risk scoring per activity ──────────────────────────────────────────────

/**
 * Evaluate a single factor for an activity and return its risk level + explanation.
 * @returns {{ level: string, explain: string, value: string|number|null }}
 */
function evaluateFactor(factorKey, factorDef, data) {
  const { weather, aqi, nwsAlerts } = data;
  const periods = weather?.properties?.periods || [];
  const currentPeriod = periods[0] || {};

  // ─── AQI ───
  if (factorKey === "aqi") {
    const maxAqi = Array.isArray(aqi)
      ? Math.max(...aqi.map((a) => a.AQI || 0))
      : null;
    if (maxAqi === null) return { level: "green", explain: "No AQI data available", value: "N/A" };
    if (factorDef.red && maxAqi >= factorDef.red.min)
      return { level: "red", explain: factorDef.red.explain, value: maxAqi };
    if (factorDef.orange && maxAqi >= factorDef.orange.min)
      return { level: "orange", explain: factorDef.orange.explain, value: maxAqi };
    if (factorDef.yellow && maxAqi >= factorDef.yellow.min)
      return { level: "yellow", explain: factorDef.yellow.explain, value: maxAqi };
    return { level: "green", explain: `AQI ${maxAqi}: Good`, value: maxAqi };
  }

  // ─── Wind ───
  if (factorKey === "wind") {
    const windMph = parseWindSpeed(currentPeriod.windSpeed);
    if (factorDef.red && windMph >= factorDef.red.min)
      return { level: "red", explain: factorDef.red.explain, value: `${windMph} mph` };
    if (factorDef.orange && windMph >= factorDef.orange.min)
      return { level: "orange", explain: factorDef.orange.explain, value: `${windMph} mph` };
    if (factorDef.yellow && windMph >= factorDef.yellow.min)
      return { level: "yellow", explain: factorDef.yellow.explain, value: `${windMph} mph` };
    return { level: "green", explain: `Wind ${windMph} mph: Normal`, value: `${windMph} mph` };
  }

  // ─── Temperature high ───
  if (factorKey === "tempHigh") {
    const temp = currentPeriod.temperature;
    if (temp == null) return { level: "green", explain: "No temp data", value: "N/A" };
    if (factorDef.red && temp >= factorDef.red.min)
      return { level: "red", explain: factorDef.red.explain, value: `${temp}°F` };
    if (factorDef.orange && temp >= factorDef.orange.min)
      return { level: "orange", explain: factorDef.orange.explain, value: `${temp}°F` };
    if (factorDef.yellow && temp >= factorDef.yellow.min)
      return { level: "yellow", explain: factorDef.yellow.explain, value: `${temp}°F` };
    return { level: "green", explain: `Temp ${temp}°F: Normal range`, value: `${temp}°F` };
  }

  // ─── Temperature low ───
  if (factorKey === "tempLow") {
    // Look at all periods to find the lowest
    const temps = periods.filter((p) => p.temperature != null).map((p) => p.temperature);
    const minTemp = temps.length ? Math.min(...temps) : null;
    if (minTemp == null) return { level: "green", explain: "No temp data", value: "N/A" };
    if (factorDef.red && minTemp <= factorDef.red.max)
      return { level: "red", explain: factorDef.red.explain, value: `${minTemp}°F` };
    if (factorDef.orange && minTemp <= factorDef.orange.max)
      return { level: "orange", explain: factorDef.orange.explain, value: `${minTemp}°F` };
    if (factorDef.yellow && minTemp <= factorDef.yellow.max)
      return { level: "yellow", explain: factorDef.yellow.explain, value: `${minTemp}°F` };
    return { level: "green", explain: `Low temp ${minTemp}°F: Normal range`, value: `${minTemp}°F` };
  }

  // ─── Lightning / keyword-based weather ───
  if (factorKey === "lightning" || factorKey === "rain") {
    if (factorDef.red && forecastContains(periods, factorDef.red.keyword))
      return { level: "red", explain: factorDef.red.explain, value: factorDef.red.keyword };
    if (factorDef.orange && forecastContains(periods, factorDef.orange.keyword))
      return { level: "orange", explain: factorDef.orange.explain, value: factorDef.orange.keyword };
    if (factorDef.yellow && forecastContains(periods, factorDef.yellow.keyword))
      return { level: "yellow", explain: factorDef.yellow.explain, value: factorDef.yellow.keyword };
    return { level: "green", explain: "No severe weather keywords in forecast", value: "Clear" };
  }

  // ─── Snow ───
  if (factorKey === "snow") {
    if (factorDef.red && forecastContains(periods, factorDef.red.keyword))
      return { level: "red", explain: factorDef.red.explain, value: factorDef.red.keyword };
    if (factorDef.orange && forecastContains(periods, factorDef.orange.keyword))
      return { level: "orange", explain: factorDef.orange.explain, value: factorDef.orange.keyword };
    if (factorDef.yellow && forecastContains(periods, factorDef.yellow.keyword))
      return { level: "yellow", explain: factorDef.yellow.explain, value: factorDef.yellow.keyword };
    return { level: "green", explain: "No snow in forecast", value: "Clear" };
  }

  // ─── NWS active alerts ───
  if (factorKey === "nwsAlert") {
    if (!nwsAlerts || nwsAlerts.length === 0)
      return { level: "green", explain: "No active NWS alerts", value: "None" };
    const worst = worstNWSSeverity(nwsAlerts);
    const headline = nwsAlerts[0]?.headline || nwsAlerts[0]?.event || "Alert active";
    if (worst === "extreme" || worst === "severe")
      return { level: "red", explain: `${factorDef.red?.explain || "Severe NWS alert"} — ${headline}`, value: headline };
    if (worst === "moderate")
      return { level: "orange", explain: `${factorDef.orange?.explain || "Moderate NWS alert"} — ${headline}`, value: headline };
    if (worst === "minor")
      return { level: "yellow", explain: `${factorDef.yellow?.explain || "Minor NWS alert"} — ${headline}`, value: headline };
    return { level: "green", explain: "No significant NWS alerts", value: "None" };
  }

  // ─── NPS alert (requires npsAlerts to be passed in data) ───
  // Disabled: Park alerts are commented out until location-in-park detection is implemented.
  // To re-enable, add logic to check if coordinates fall within a park boundary, then fetch and assess NPS alerts.
  // if (factorKey === "npsAlert") {
  //   const npsAlerts = data.npsAlerts || [];
  //   if (!npsAlerts.length) return { level: "green", explain: "No NPS alerts", value: "None" };
  //   // Check for worst category
  //   const cats = npsAlerts.map((a) => (a.category || "").toLowerCase());
  //   if (cats.includes("danger"))
  //     return { level: "red", explain: factorDef.red.explain, value: npsAlerts.find(a => (a.category||"").toLowerCase() === "danger")?.title || "Danger" };
  //   if (cats.includes("closure") || cats.includes("park closure"))
  //     return { level: "orange", explain: factorDef.orange.explain, value: npsAlerts.find(a => (a.category||"").toLowerCase().includes("closure"))?.title || "Closure" };
  //   if (cats.includes("caution"))
  //     return { level: "yellow", explain: factorDef.yellow.explain, value: npsAlerts.find(a => (a.category||"").toLowerCase() === "caution")?.title || "Caution" };
  //   return { level: "green", explain: "NPS alerts are informational only", value: "Info" };
  // }

  // ─── River flow (placeholder — needs USGS data passed in) ───
  if (factorKey === "riverFlow" || factorKey === "gageHeight") {
    // For the prototype we check NWS alerts for flood-related keywords as a proxy
    const floodAlerts = (nwsAlerts || []).filter((a) =>
      (a.event || "").toLowerCase().includes("flood")
    );
    if (floodAlerts.length > 0) {
      const event = floodAlerts[0].event;
      if (event.toLowerCase().includes("warning"))
        return { level: "red", explain: `${event} active`, value: event };
      if (event.toLowerCase().includes("watch"))
        return { level: "orange", explain: `${event} in effect`, value: event };
      return { level: "yellow", explain: `${event} advisory`, value: event };
    }
    return { level: "green", explain: "No flood alerts detected", value: "Normal" };
  }

  return { level: "green", explain: "Factor not evaluated", value: "N/A" };
}

/**
 * Compute risk assessment for all activities given fetched data.
 * @param {object} data - from fetchRiskData()
 * @returns {object} { [activityKey]: { overall, factors: { [factorKey]: { level, explain, value } } } }
 */
export function computeAllActivityRisks(data) {
  const results = {};

  for (const [activityKey, activityDef] of Object.entries(activityThresholds)) {
    const factors = {};
    let overallIdx = 0; // green

    for (const [factorKey, factorDef] of Object.entries(activityDef.factors)) {
      const result = evaluateFactor(factorKey, factorDef, data);
      factors[factorKey] = {
        ...result,
        label: factorDef.label,
        unit: factorDef.unit || null,
      };
      const idx = RISK_LEVELS.indexOf(result.level);
      if (idx > overallIdx) overallIdx = idx;
    }

    results[activityKey] = {
      label: activityDef.label,
      overall: RISK_LEVELS[overallIdx],
      factors,
    };
  }

  return results;
}
